import { useFormularioSecretariaMunicipalAgriculturaStore } from '../../../../../../stores/forms/formularioAgricultura.store';
import { useMemo, useState } from 'react';

interface GrupoEstrutura {
  id: string;
  titulo: string;
  isOpen: boolean;
  items: Array<{ label: string; key: string; qtdKey: string }>;
}

export const useStep5 = () => {
  const { formulario, setPartialForm, salvarRascunho } = useFormularioSecretariaMunicipalAgriculturaStore();
  const [errosQuantidade, setErrosQuantidade] = useState<Set<string>>(new Set());
  const [gruposAbertos, setGruposAbertos] = useState<Set<string>>(new Set(['infraestrutura'])); // initially only first group open

  const stepAtual = formulario?.step_atual ?? 1;
  const totalSteps = 8;

  const estruturas = useMemo(() => [
    // Infraestrutura Predial e Instalações
    { label: 'Prédio Próprio', key: 'possui_predio_proprio', qtdKey: 'quantidade_predio_proprio' },
    { label: 'Prédio Alugado', key: 'possui_predio_alugado', qtdKey: 'quantidade_predio_alugado' },
    { label: 'Prédio Cedido de Outro Órgão', key: 'possui_predio_cedido', qtdKey: 'quantidade_predio_cedido' },
    { label: 'Galpão / Armazém de Insumos', key: 'possui_galpao', qtdKey: 'quantidade_galpao' },
    { label: 'Viveiro Municipal / Estufa', key: 'viveiro_producao_mudas', qtdKey: 'quantidade_viveiro_producao_mudas' },
    { label: 'Auditório / Sala de Reunião', key: 'possui_auditorio', qtdKey: 'quantidade_auditorio' },
    { label: 'Cozinha Piloto / Refeitório', key: 'possui_cozinha_piloto', qtdKey: 'quantidade_cozinha_piloto' },
    // Tecnologia e Engenharia
    { label: 'Computador de Mesa (Desktop)', key: 'possui_computador', qtdKey: 'quantidade_computador' },
    { label: 'Notebook', key: 'possui_notebook', qtdKey: 'quantidade_notebook' },
    { label: 'Impressora / Multifuncional', key: 'possui_impressora', qtdKey: 'quantidade_impressora' },
    { label: 'Tablet', key: 'possui_tablet', qtdKey: 'quantidade_tablet' },
    { label: 'Receptor GPS de Navegação (Portátil)', key: 'possui_receptor_gps_navegacao', qtdKey: 'quantidade_receptor_gps_navegacao' },
    { label: 'Receptor GPS Geodésico (Precisão/Topografia)', key: 'possui_receptor_gps_geodesico', qtdKey: 'quantidade_receptor_gps_geodesico' },
    { label: 'Equipamento de Coleta / Análise de Solo', key: 'possui_equipamento_coleta_solo', qtdKey: 'quantidade_equipamento_coleta_solo' },
    { label: 'Drone para Mapeamento', key: 'possui_drone', qtdKey: 'quantidade_drone' },
    // Frota Leve e Administrativa
    { label: 'Veículo de Passeio (Hatch/Sedan)', key: 'possui_veiculo_popular', qtdKey: 'quantidade_veiculo_popular' },
    { label: 'Caminhonete (Pick-up leve)', key: 'possui_veiculo_caminhonete', qtdKey: 'quantidade_veiculo_caminhonete' },
    { label: 'Caminhonete Traçada 4x4', key: 'possui_caminhonete_tracada', qtdKey: 'quantidade_caminhonete_tracada' },
    { label: 'SUV', key: 'possui_veiculo_suv', qtdKey: 'quantidade_veiculo_suv' },
    { label: 'Motocicleta', key: 'possui_veiculo_motocicleta', qtdKey: 'quantidade_veiculo_motocicleta' },
    // Frota Pesada e Caminhões
    { label: 'Caminhão Caçamba', key: 'possui_caminhao_cacamba', qtdKey: 'quantidade_caminhao_cacamba' },
    { label: 'Caminhão Baú', key: 'possui_caminhao_bau_sem_refrigeracao', qtdKey: 'quantidade_caminhao_bau_sem_refrigeracao' },
    { label: 'Caminhão Baú Refrigerado', key: 'possui_caminhao_bau_refrigerado', qtdKey: 'quantidade_caminhao_bau_refrigerado' },
    { label: 'Caminhão Pipa', key: 'possui_caminhao_pipa', qtdKey: 'quantidade_caminhao_pipa' },
    { label: 'Caminhão Tanque Isotérmico', key: 'possui_caminhao_com_tanque_isotermico', qtdKey: 'quantidade_caminhao_com_tanque_isotermico' },
    { label: 'Caminhão Prancha', key: 'possui_caminhao_prancha', qtdKey: 'quantidade_caminhao_prancha' },
    // Maquinário Agrícola e Pesado
    { label: 'Microtrator', key: 'possui_trator_14cv', qtdKey: 'quantidade_trator_14cv' },
    { label: 'Trator Agrícola (Linha leve/média)', key: 'possui_trator_50cv', qtdKey: 'quantidade_trator_50cv' },
    { label: 'Trator Agrícola (Linha pesada)', key: 'possui_trator_75cv', qtdKey: 'quantidade_trator_75cv' },
    { label: 'Retroescavadeira', key: 'retroescavadeira', qtdKey: 'quantidade_retroescavadeira' },
    { label: 'Escavadeira Hidráulica', key: 'escavadeira_hidraulica', qtdKey: 'quantidade_escavadeira_hidraulica' },
    { label: 'Pá Carregadeira', key: 'pa_carregadeira', qtdKey: 'quantidade_pa_carregadeira' },
    { label: 'Motoniveladora', key: 'possui_motoniveladora', qtdKey: 'quantidade_motoniveladora' },
    { label: 'Grade Aradora / Niveladora', key: 'possui_grade_aradora', qtdKey: 'quantidade_grade_aradora' },
    { label: 'Plantadeira / Semeadeira', key: 'possui_plantadeira', qtdKey: 'quantidade_plantadeira' },
    { label: 'Distribuidor de Calcário / Adubo', key: 'possui_distribuidor_calcario', qtdKey: 'quantidade_distribuidor_calcario' },
  ], [/* static */]);

  const grupos = useMemo<GrupoEstrutura[]>(() => [
    {
      id: 'infraestrutura',
      titulo: 'Infraestrutura Predial e Instalações',
      isOpen: gruposAbertos.has('infraestrutura'),
      items: [
        { label: 'Prédio Próprio', key: 'possui_predio_proprio', qtdKey: 'quantidade_predio_proprio' },
        { label: 'Prédio Alugado', key: 'possui_predio_alugado', qtdKey: 'quantidade_predio_alugado' },
        { label: 'Prédio Cedido de Outro Órgão', key: 'possui_predio_cedido', qtdKey: 'quantidade_predio_cedido' },
        { label: 'Galpão / Armazém de Insumos', key: 'possui_galpao', qtdKey: 'quantidade_galpao' },
        { label: 'Viveiro Municipal / Estufa', key: 'viveiro_producao_mudas', qtdKey: 'quantidade_viveiro_producao_mudas' },
        { label: 'Auditório / Sala de Reunião', key: 'possui_auditorio', qtdKey: 'quantidade_auditorio' },
        { label: 'Cozinha Piloto / Refeitório', key: 'possui_cozinha_piloto', qtdKey: 'quantidade_cozinha_piloto' },
      ],
    },
    {
      id: 'tecnologia',
      titulo: 'Tecnologia e Engenharia',
      isOpen: gruposAbertos.has('tecnologia'),
      items: [
        { label: 'Computador de Mesa (Desktop)', key: 'possui_computador', qtdKey: 'quantidade_computador' },
        { label: 'Notebook', key: 'possui_notebook', qtdKey: 'quantidade_notebook' },
        { label: 'Impressora / Multifuncional', key: 'possui_impressora', qtdKey: 'quantidade_impressora' },
        { label: 'Tablet', key: 'possui_tablet', qtdKey: 'quantidade_tablet' },
        { label: 'Receptor GPS de Navegação (Portátil)', key: 'possui_receptor_gps_navegacao', qtdKey: 'quantidade_receptor_gps_navegacao' },
        { label: 'Receptor GPS Geodésico (Precisão/Topografia)', key: 'possui_receptor_gps_geodesico', qtdKey: 'quantidade_receptor_gps_geodesico' },
        { label: 'Equipamento de Coleta / Análise de Solo', key: 'possui_equipamento_coleta_solo', qtdKey: 'quantidade_equipamento_coleta_solo' },
        { label: 'Drone para Mapeamento', key: 'possui_drone', qtdKey: 'quantidade_drone' },
      ],
    },
    {
      id: 'frota_leve',
      titulo: 'Frota Leve e Administrativa',
      isOpen: gruposAbertos.has('frota_leve'),
      items: [
        { label: 'Veículo de Passeio (Hatch/Sedan)', key: 'possui_veiculo_popular', qtdKey: 'quantidade_veiculo_popular' },
        { label: 'Caminhonete (Pick-up leve)', key: 'possui_veiculo_caminhonete', qtdKey: 'quantidade_veiculo_caminhonete' },
        { label: 'Caminhonete Traçada 4x4', key: 'possui_caminhonete_tracada', qtdKey: 'quantidade_caminhonete_tracada' },
        { label: 'SUV', key: 'possui_veiculo_suv', qtdKey: 'quantidade_veiculo_suv' },
        { label: 'Motocicleta', key: 'possui_veiculo_motocicleta', qtdKey: 'quantidade_veiculo_motocicleta' },
      ],
    },
    {
      id: 'frota_pesada',
      titulo: 'Frota Pesada e Caminhões',
      isOpen: gruposAbertos.has('frota_pesada'),
      items: [
        { label: 'Caminhão Caçamba', key: 'possui_caminhao_cacamba', qtdKey: 'quantidade_caminhao_cacamba' },
        { label: 'Caminhão Baú', key: 'possui_caminhao_bau_sem_refrigeracao', qtdKey: 'quantidade_caminhao_bau_sem_refrigeracao' },
        { label: 'Caminhão Baú Refrigerado', key: 'possui_caminhao_bau_refrigerado', qtdKey: 'quantidade_caminhao_bau_refrigerado' },
        { label: 'Caminhão Pipa', key: 'possui_caminhao_pipa', qtdKey: 'quantidade_caminhao_pipa' },
        { label: 'Caminhão Tanque Isotérmico', key: 'possui_caminhao_com_tanque_isotermico', qtdKey: 'quantidade_caminhao_com_tanque_isotermico' },
        { label: 'Caminhão Prancha', key: 'possui_caminhao_prancha', qtdKey: 'quantidade_caminhao_prancha' },
      ],
    },
    {
      id: 'maquinario',
      titulo: 'Maquinário Agrícola e Pesado',
      isOpen: gruposAbertos.has('maquinario'),
      items: [
        { label: 'Microtrator', key: 'possui_trator_14cv', qtdKey: 'quantidade_trator_14cv' },
        { label: 'Trator Agrícola (Linha leve/média)', key: 'possui_trator_50cv', qtdKey: 'quantidade_trator_50cv' },
        { label: 'Trator Agrícola (Linha pesada)', key: 'possui_trator_75cv', qtdKey: 'quantidade_trator_75cv' },
        { label: 'Retroescavadeira', key: 'retroescavadeira', qtdKey: 'quantidade_retroescavadeira' },
        { label: 'Escavadeira Hidráulica', key: 'escavadeira_hidraulica', qtdKey: 'quantidade_escavadeira_hidraulica' },
        { label: 'Pá Carregadeira', key: 'pa_carregadeira', qtdKey: 'quantidade_pa_carregadeira' },
        { label: 'Motoniveladora', key: 'possui_motoniveladora', qtdKey: 'quantidade_motoniveladora' },
        { label: 'Grade Aradora / Niveladora', key: 'possui_grade_aradora', qtdKey: 'quantidade_grade_aradora' },
        { label: 'Plantadeira / Semeadeira', key: 'possui_plantadeira', qtdKey: 'quantidade_plantadeira' },
        { label: 'Distribuidor de Calcário / Adubo', key: 'possui_distribuidor_calcario', qtdKey: 'quantidade_distribuidor_calcario' },
      ],
    },
  ], [gruposAbertos]);

  const toggleGrupo = (grupoId: string) => {
    const novosAbertos = new Set(gruposAbertos);
    if (novosAbertos.has(grupoId)) {
      novosAbertos.delete(grupoId);
    } else {
      novosAbertos.add(grupoId);
    }
    setGruposAbertos(novosAbertos);
  };

  const validarQuantidadeItem = (qtdKey: string, key: string, quantidade: number | null): boolean => {
    const estaSelecionado = (formulario as any)?.[key] === true;
    const quantidadeValida = estaSelecionado ? (quantidade !== null && quantidade !== undefined && Number(quantidade) > 0) : true;
    return quantidadeValida;
  };

  const todasAsQuantidadesValidas = () => {
    return estruturas.every(estrutura => {
      const quantidade = (formulario as any)?.[estrutura.qtdKey];
      return validarQuantidadeItem(estrutura.qtdKey, estrutura.key, quantidade);
    });
  };

  const pelomenosUmaEstruturaSelecionada = estruturas.some(
    (estrutura) => (formulario as any)?.[estrutura.key] === true,
  );

  const validarCamposObrigatorios = () => {
    // Only validate that at least one structure is selected and all quantities are valid
    if (!pelomenosUmaEstruturaSelecionada) return false;
    if (!todasAsQuantidadesValidas()) return false;
    return true;
  };

  const isValid = validarCamposObrigatorios();

  const handleEstrutura = (key: string, checked: boolean) => {
    setPartialForm(key as any, checked);

    // Find the corresponding qtdKey
    const estrutura = estruturas.find((e) => e.key === key);

    if (!checked) {
      // When unchecking, clear the quantity and remove any error
      if (estrutura) {
        setPartialForm(estrutura.qtdKey as any, null);
        // Remove error for this quantity field
        const novoErros = new Set(errosQuantidade);
        novoErros.delete(estrutura.qtdKey);
        setErrosQuantidade(novoErros);
      }
    }
  };

  const handleQuantidade = (qtdKey: string, valor: string) => {
    const valorNumerico = valor.replace(/\D/g, '');
    const valorFinal = valorNumerico === '' ? null : Number(valorNumerico);
    setPartialForm(qtdKey as any, valorFinal);
  };

  const handleQuantidadeBlur = (qtdKey: string, key: string) => {
    const quantidade = (formulario as any)?.[qtdKey];
    const estaSelecionado = (formulario as any)?.[key] === true;

    // Only validate if the item is checked
    if (estaSelecionado) {
      const quantidadeValida = quantidade !== null && quantidade !== undefined && Number(quantidade) > 0;

      const novoErros = new Set(errosQuantidade);
      if (!quantidadeValida) {
        novoErros.add(qtdKey);
      } else {
        novoErros.delete(qtdKey);
      }
      setErrosQuantidade(novoErros);
    }
  };

  const handleAvancar = async () => {
    if (!isValid) return;
    setPartialForm('step_atual', stepAtual + 1);
    await salvarRascunho();
  };

  const handleVoltar = async () => {
    setPartialForm('step_atual', Math.max(1, stepAtual - 1));
    await salvarRascunho();
  };

  return {
    formulario,
    estruturas,
    grupos,
    isValid,
    handleEstrutura,
    handleQuantidade,
    handleQuantidadeBlur,
    errosQuantidade,
    toggleGrupo,
    handleAvancar,
    handleVoltar,
    setPartialForm,
    stepAtual,
    totalSteps,
  };
};
